import { CircleHelp, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button as RACButton, TooltipTrigger } from "react-aria-components";

import type { MediaItem } from "@/components/media-preview-modal/mediaPreviewModal.types";

import { Button } from "@/components/button";
import { MediaPreviewModalProvider } from "@/components/media-preview-modal/MediaPreviewModalProvider";
import { useMediaPreviewModal } from "@/components/media-preview-modal/useMediaPreviewModal";
import { Switch } from "@/components/switch";
import { Tooltip } from "@/components/tooltip";
import { useMentionableUsers } from "@/features/my-files/components/upload-files-modal/hooks/useMentionableUsers";
import { getExtensionFromFilename } from "@/utils/getExtensionFromMime.ts";

import type { FileResult } from "../types";

import { ChatTextarea } from "../../edit/components/chat-section/components/chat-input/ChatTextArea";
import { useDcc } from "../contexts/DesignConfidenceCheckerProvider";
import { useObjectUrl } from "../hooks/useObjectUrl";

interface MessageStepProps {
  onBack: () => void;
  onClose: () => void;
  onSubmit: () => void;
}

export function MessageStep(props: MessageStepProps) {
  return (
    <MediaPreviewModalProvider>
      <MessageStepBody {...props} />
    </MediaPreviewModalProvider>
  );
}

function MessageStepBody({ onBack, onClose, onSubmit }: MessageStepProps) {
  const { deliveryMessage, requestId, session, shell } = useDcc();
  const results = session.results;
  const { isSubmitting } = shell;
  const {
    isSendToChat,
    message,
    setIsSendToChat,
    setMessage: onMessageChange,
  } = deliveryMessage;
  const onSendToChatChange = setIsSendToChat;
  const { data: mentionableUsers } = useMentionableUsers(requestId);
  const { setMediaItems } = useMediaPreviewModal();

  // Feed the shared preview modal the delivered design files (already on S3 by
  // this step, so we drive it by attachmentId + remote URL — same as My Files).
  const mediaItems = useMemo<MediaItem[]>(
    () =>
      results.map((result) => ({
        extension: getExtensionFromFilename(result.file.name),
        id: result.id,
        name: result.file.name,
        url: result.remoteUrl ?? "",
      })),
    [results],
  );

  useEffect(() => {
    setMediaItems(mediaItems);
  }, [mediaItems, setMediaItems]);

  return (
    <div className="flex h-full flex-col">
      <MessageStepHeader onClose={onClose} />
      <div className="flex flex-1 gap-6 overflow-hidden p-6">
        <FilesPanel results={results} />
        <MessageEditor
          mentionableUsers={mentionableUsers}
          message={message}
          onMessageChange={onMessageChange}
        />
      </div>
      <MessageStepFooter
        isSendToChat={isSendToChat}
        isSubmitting={isSubmitting}
        onBack={onBack}
        onSendToChatChange={onSendToChatChange}
        onSubmit={onSubmit}
      />
    </div>
  );
}

function MessageStepHeader({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex items-start justify-between border-b border-border-secondary px-6 py-5">
      <div className="flex flex-col gap-0.5">
        <span className="text-base font-semibold text-text-primary">
          Input your message for the client
        </span>
        <span className="text-sm text-text-tertiary">
          Confirm your designs and message before sending them to the client.
        </span>
      </div>
      <button
        aria-label="Close"
        className="rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-bg-secondary"
        onClick={onClose}
        type="button"
      >
        <X size={20} />
      </button>
    </div>
  );
}

function FilesPanel({ results }: { results: FileResult[] }) {
  return (
    <div className="flex w-[280px] shrink-0 flex-col gap-3 overflow-y-auto">
      <span className="text-sm font-semibold text-text-primary">
        List of uploaded files
      </span>
      <div className="flex flex-wrap gap-2">
        {results.map((result) => (
          <FileThumbnail key={result.id} result={result} />
        ))}
      </div>
    </div>
  );
}

function FileThumbnail({ result }: { result: FileResult }) {
  const { openModal } = useMediaPreviewModal();
  const objectUrl = useObjectUrl(result.file);

  const sources = useMemo(
    () =>
      [result.previewUrl, result.remoteUrl, objectUrl].filter(
        (source) => source !== null && source !== "",
      ),
    [objectUrl, result.previewUrl, result.remoteUrl],
  );
  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    setSourceIndex(0);
  }, [sources]);

  const thumbnailSrc = sources[sourceIndex];

  return (
    <button
      aria-label={`Preview ${result.file.name}`}
      className="h-[60px] w-[60px] shrink-0 cursor-pointer overflow-hidden rounded-md border border-border-secondary bg-bg-secondary transition-opacity hover:opacity-80"
      onClick={() => openModal(result.id)}
      type="button"
    >
      {thumbnailSrc && (
        <img
          alt={result.file.name}
          className="h-full w-full object-cover"
          onError={() => {
            setSourceIndex((index) => Math.min(index + 1, sources.length - 1));
          }}
          src={thumbnailSrc}
        />
      )}
    </button>
  );
}

interface MessageEditorProps {
  mentionableUsers: { id: string; label: string }[] | undefined;
  message: string;
  onMessageChange: (value: string) => void;
}

function MessageEditor({
  mentionableUsers,
  message,
  onMessageChange,
}: MessageEditorProps) {
  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto">
      <div className="flex flex-col gap-1">
        <span className="text-base font-semibold text-text-primary">
          Input your message for the client
        </span>
        <span className="text-sm text-text-tertiary">
          Include any specific details that you want to share based on your
          design process, and tag the client to notify them.
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-text-primary">
          Your message
        </span>
        <ChatTextarea
          className="min-h-[260px]"
          mentionSuggestions={mentionableUsers}
          onChange={onMessageChange}
          placeholder="Write a message for the client..."
          showBubbleMenu={false}
          showMenuBar
          value={message}
        />
      </div>
    </div>
  );
}

interface MessageStepFooterProps {
  isSendToChat: boolean;
  isSubmitting: boolean;
  onBack: () => void;
  onSendToChatChange: (value: boolean) => void;
  onSubmit: () => void;
}

function MessageStepFooter({
  isSendToChat,
  isSubmitting,
  onBack,
  onSendToChatChange,
  onSubmit,
}: MessageStepFooterProps) {
  return (
    <div className="flex items-center justify-end border-t border-border-secondary px-6 py-4">
      <div className="flex items-center gap-1 mr-xl">
        <div className="flex items-center gap-2">
          <Switch
            aria-label="Send to chat"
            isSelected={isSendToChat}
            onChange={onSendToChatChange}
          />
          <span className="text-sm text-text-secondary">Send to chat</span>
        </div>
        <TooltipTrigger delay={0}>
          <RACButton>
            <CircleHelp className="text-fg-quinary" size={16} />
          </RACButton>
          <Tooltip>
            {isSendToChat ? (
              <div className="flex flex-col gap-1">
                <span>Enable Send to Chat</span>
                <span className="font-medium">
                  The files will be sent to the chat and the customer will be
                  able to view and add Feedback Tool comments to request for
                  revisions.
                </span>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <span>Disable Send to Chat</span>
                <span className="font-medium">
                  The files will only be uploaded in My Files. The client will
                  not be able to see them in the chat, or use the Feedback Tool
                  to request for revisions.
                </span>
              </div>
            )}
          </Tooltip>
        </TooltipTrigger>
      </div>
      <div className="flex items-center gap-3">
        <Button color="gray" onPress={onBack} size="lg" variant="secondary">
          Back
        </Button>
        <Button
          color="color"
          isDisabled={isSubmitting}
          onPress={onSubmit}
          size="lg"
          variant="primary"
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
